import type { ReactNode } from 'react';
import { Panel } from '../../kit';
import { useT } from '../../../messages';
import type { T } from '../../../messages';
import { doneCount } from './steps';
import type { Checklist as ChecklistData, Step, Tick, Who } from './steps';
import { follow } from './go';
import type { Moves } from './go';

function tickWord(t: T, tick: Tick): string {
  switch (tick) {
    case 'done':
      return t('overview.tick.done');
    case 'wait':
      return t('overview.tick.wait');
    case 'open':
      return t('overview.tick.open');
  }
}

function whoWords(t: T, who: Who): string | null {
  switch (who) {
    case 'operator':
      return t('overview.who.operator');
    case 'owner':
      return t('overview.who.owner');
    case 'platform':
      return t('overview.who.platform');
    default:
      return null;
  }
}

function StepRow({ step, moves }: { step: Step; moves: Moves }): ReactNode {
  const t = useT();
  const who = step.tick === 'done' ? null : whoWords(t, step.who);
  return (
    <li className={`ov-step ov-step--${step.tick}`} data-step={step.id}>
      <span
        className={`ov-tick ov-tick--${step.tick}`}
        role="img"
        aria-label={tickWord(t, step.tick)}
      />
      <div>
        <div className="ov-step__title">{step.title}</div>
        <div className="ov-step__detail">{step.detail}</div>
        {who === null ? null : <div className="ov-step__who">{who}</div>}
      </div>
      {step.tick === 'done' || step.action === null ? (
        <span />
      ) : (
        <button
          type="button"
          className="button button--sm"
          onClick={() => {
            if (step.action !== null) follow(moves, step.action.go);
          }}
        >
          {step.action.label}
        </button>
      )}
    </li>
  );
}

/**
 * **"Getting live"** — the go-live plan for this business, one step per line,
 * each with its tick, who it waits on, and the way to where it gets done.
 *
 * The online stage is a second, folded list underneath: the mockup's note is
 * that its steps belong to the Website and Connections tabs and must not
 * clutter the first list, but an operator should still be able to see how far
 * along it is without leaving the page. It opens by itself once the business
 * is live, because that is when it becomes the plan.
 */
export function ChecklistPanel({
  list,
  status,
  moves,
}: {
  list: ChecklistData;
  status: string;
  moves: Moves;
}): ReactNode {
  const t = useT();
  return (
    <Panel
      title={t('overview.checklist.title')}
      className="ov-checklist"
      actions={
        <span className="muted">
          {t('overview.checklist.count', { done: doneCount(list.live), total: list.live.length })}
        </span>
      }
    >
      <ul className="ov-steps">
        {list.live.map((s) => (
          <StepRow key={s.id} step={s} moves={moves} />
        ))}
      </ul>
      <details className="ov-online" open={status === 'live'}>
        <summary>
          <span className="ov-online__title">{t('overview.online.title')}</span>{' '}
          <span className="muted">
            {t('overview.checklist.count', {
              done: doneCount(list.online),
              total: list.online.length,
            })}
          </span>
        </summary>
        <p className="muted ov-online__note">{t('overview.online.note')}</p>
        <ul className="ov-steps">
          {list.online.map((s) => (
            <StepRow key={s.id} step={s} moves={moves} />
          ))}
        </ul>
      </details>
    </Panel>
  );
}
